import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  Typography,
  OutlinedInput,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Images } from "../../utils/Images";
import CurrencySelectorPopup from "./CurrencySelectorPopup";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { validateDonationAmount, sanitizeAmountInput } from "../../utils/donationValidation";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const paymentOptions = [
  { name: "PhonePe", src: Images.phonepayIcon },
  { name: "Gpay", src: Images.gpayIcon },
  { name: "Bhim UPI", src: Images.upiIcon },
  { name: "More", src: Images.moreIcon },
];

const DonationBox = ({ campaignTitle, campaignId, isMonthlyDonation = false, onDonationAmountChange, initialAmount = 3000 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const [baseAmount, setBaseAmount] = useState(initialAmount); // Base donation amount (without products) - prefilled default
  const [productTotal, setProductTotal] = useState(0); // Total amount from selected products
  const [selectedBaseAmount, setSelectedBaseAmount] = useState(initialAmount); // Track which base amount button is selected
  const [hadProductsBefore, setHadProductsBefore] = useState(false); // Track if products were ever selected
  const amountButtonsRef = useRef(null);
  // Initial state will be updated when CMS data is loaded
  const [displayedAmounts, setDisplayedAmounts] = useState([2000, 4000, 6000, 8000, 10000]);
  
  // Calculate total amount (base + products)
  const totalAmount = baseAmount + productTotal;

  // Notify parent (e.g. campaign detail page) so top "Donate Now" can pass this amount
  useEffect(() => {
    if (typeof onDonationAmountChange === "function") {
      onDonationAmountChange(totalAmount);
    }
  }, [totalAmount, onDonationAmountChange]);

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Extract custom donation amounts from CMS API
  const customAmounts = useMemo(() => {
    if (!cmsHome?.data || !Array.isArray(cmsHome.data)) {
      // Fallback to default amounts if CMS data is not available
      return [2000, 4000, 6000, 8000, 10000];
    }

    // Find the donation box custom amounts section (admin may call it "Custom Donation Amount Boxes" or "Donation Box Custom Amount")
    const sectionMatch = (section) =>
      (section?.section === "Donation Box Custom Amount" || section?.section === "Custom Donation Amount Boxes") &&
      (section?.status === 1 || section?.status === true);
    let donationBoxSection = cmsHome.data.find(
      (section) => (section?.page === "Donation Box" || section?.page === "Donation") && sectionMatch(section)
    );
    if (!donationBoxSection) {
      donationBoxSection = cmsHome.data.find(sectionMatch);
    }

    if (!donationBoxSection) {
      // Fallback to default amounts if section not found
      return [2000, 4000, 6000, 8000, 10000];
    }

    const amounts = [];
    const seenAmounts = new Set(); // Track seen amounts to avoid duplicates while preserving order

    // Extract amounts from main section fields in order
    if (donationBoxSection.section_title) {
      const amount = Number(donationBoxSection.section_title);
      if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
        amounts.push(amount);
        seenAmounts.add(amount);
      }
    }
    if (donationBoxSection.title) {
      const amount = Number(donationBoxSection.title);
      if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
        amounts.push(amount);
        seenAmounts.add(amount);
      }
    }
    if (donationBoxSection.sub_title) {
      const amount = Number(donationBoxSection.sub_title);
      if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
        amounts.push(amount);
        seenAmounts.add(amount);
      }
    }
    if (donationBoxSection.description) {
      const amount = Number(donationBoxSection.description);
      if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
        amounts.push(amount);
        seenAmounts.add(amount);
      }
    }

    // Extract amounts from relatedCMS array in order
    if (donationBoxSection.relatedCMS && Array.isArray(donationBoxSection.relatedCMS)) {
      donationBoxSection.relatedCMS.forEach((item) => {
        if (item && (item.status === 1 || item.status === true)) {
          if (item.title) {
            const amount = Number(item.title);
            if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
              amounts.push(amount);
              seenAmounts.add(amount);
            }
          }
          if (item.stand_title) {
            const amount = Number(item.stand_title);
            if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
              amounts.push(amount);
              seenAmounts.add(amount);
            }
          }
          if (item.sub_title) {
            const amount = Number(item.sub_title);
            if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
              amounts.push(amount);
              seenAmounts.add(amount);
            }
          }
          if (item.description) {
            const amount = Number(item.description);
            if (!isNaN(amount) && amount > 0 && !seenAmounts.has(amount)) {
              amounts.push(amount);
              seenAmounts.add(amount);
            }
          }
        }
      });
    }

    return amounts.length > 0 ? amounts : [1000, 2000, 4000, 6000, 8000, 10000];
  }, [cmsHome]);

  // Use custom amounts from CMS, fallback to generated amounts if needed
  const allAmounts = useMemo(() => {
    // If we have custom amounts from CMS, use them
    if (customAmounts && customAmounts.length > 0) {
      return customAmounts;
    }
    // Fallback: Generate amounts in multiples of 2000
    const amounts = [];
    for (let i = 2000; i <= 50000; i += 2000) {
      amounts.push(i);
    }
    return amounts;
  }, [customAmounts]);

  const currencyList = [
    { code: "INR", symbol: "₹" },
    { code: "USD", symbol: "$" },
    { code: "AED", symbol: "د.إ" },
    { code: "CAD", symbol: "C$" },
    { code: "AUD", symbol: "A$" },
    { code: "GBP", symbol: "£" },
  ];
  const [currency, setCurrency] = useState(currencyList[0]); // Always start with INR
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  const [hasProducts, setHasProducts] = useState(false); // Track if products are selected
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar open state
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isBelow600 = useMediaQuery("(max-width: 599px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 899px)"); // 600-900px range
  const isAbove1441 = useMediaQuery("(min-width: 1441px)");
  const isBelow1411 = useMediaQuery("(max-width: 1411px)");
  const isBelow1023 = useMediaQuery("(max-width: 1023px)"); // ≤ 1023px
  const isAbove1024 = useMediaQuery("(min-width: 1024px)"); // ≥ 1024px
  const isBelow1099 = useMediaQuery("(max-width: 1099px)");

  // Calculate how many buttons can fit based on available space
  useEffect(() => {
    const calculateVisibleAmounts = (isSecondPass = false) => {
      if (isMobile) {
        // On mobile, show fewer buttons (first 5 from custom amounts or defaults)
        const mobileAmounts = allAmounts.slice(0, 5);
        setDisplayedAmounts(mobileAmounts.length > 0 ? mobileAmounts : [2000, 4000, 6000, 8000, 10000]);
        return;
      }

      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!amountButtonsRef.current) {
            // Show first 6 from custom amounts or defaults
            const defaultAmounts = allAmounts.slice(0, 6);
            setDisplayedAmounts(defaultAmounts.length > 0 ? defaultAmounts : [2000, 4000, 6000, 8000, 10000, 12000]);
            return;
          }

          const container = amountButtonsRef.current;
          const parentContainer = container.parentElement;
          
          if (!parentContainer) {
            // Show first 6 from custom amounts or defaults
            const defaultAmounts = allAmounts.slice(0, 6);
            setDisplayedAmounts(defaultAmounts.length > 0 ? defaultAmounts : [2000, 4000, 6000, 8000, 10000, 12000]);
            return;
          }

          // Get the parent container width (the middle + right section)
          const parentWidth = parentContainer.offsetWidth;
          
          // Get the right section (input + donate button) - it's the next sibling
          const rightSection = container.nextElementSibling;
          const rightSectionWidth = rightSection ? rightSection.offsetWidth : 0;
          
          // Calculate available width for amount buttons
          // Account for gaps between elements
          const gap = isBelow900 ? 10 : (isAbove1200 ? 20 : 10);
          const availableWidth = Math.max(0, parentWidth - rightSectionWidth - gap);
          
          // Measure actual button widths if buttons already exist
          const existingButtons = container.querySelectorAll('button');
          let averageButtonWidth = 0;
          let actualButtonGap = 0;
          
          if (existingButtons.length > 0) {
            // Calculate average button width from all existing buttons
            // This accounts for different widths (e.g., ₹200 vs ₹5,000)
            let totalWidth = 0;
            existingButtons.forEach((btn) => {
              totalWidth += btn.getBoundingClientRect().width;
            });
            averageButtonWidth = totalWidth / existingButtons.length;
            
            // Get gap between buttons if there are multiple
            if (existingButtons.length > 1) {
              const firstButton = existingButtons[0];
              const secondButton = existingButtons[1];
              const firstRect = firstButton.getBoundingClientRect();
              const secondRect = secondButton.getBoundingClientRect();
              actualButtonGap = secondRect.left - firstRect.right;
            } else {
              actualButtonGap = isMobile ? 6 : (isAbove1200 ? 20 : 10);
            }
          } else {
            // Fallback estimates if no buttons exist yet
            // Use a conservative estimate that accounts for longer numbers
            averageButtonWidth = isBelow1099 ? 85 : 100;
            actualButtonGap = isMobile ? 6 : (isAbove1200 ? 20 : 10);
          }
          
          // Calculate how many buttons can fit
          // Use a more conservative approach to prevent overflow
          const totalButtonWidth = averageButtonWidth + actualButtonGap;
          const maxButtons = Math.floor(availableWidth / totalButtonWidth);
          
          // Ensure we don't exceed available space - be conservative
          // Leave some buffer space (at least one gap width) to prevent overflow
          const bufferSpace = actualButtonGap;
          const adjustedAvailableWidth = Math.max(0, availableWidth - bufferSpace);
          const safeMaxButtons = Math.floor(adjustedAvailableWidth / totalButtonWidth);
          
          // If this is the second pass, use actual measurements to fine-tune
          if (isSecondPass && existingButtons.length > 0) {
            // Calculate total width used by current buttons
            let totalUsedWidth = 0;
            existingButtons.forEach((btn, index) => {
              totalUsedWidth += btn.getBoundingClientRect().width;
              if (index > 0) {
                totalUsedWidth += actualButtonGap;
              }
            });
            
            const remainingWidth = availableWidth - totalUsedWidth;
            
            // Only add another button if there's enough space (with buffer)
            if (remainingWidth >= (averageButtonWidth + actualButtonGap + bufferSpace)) {
              const newCount = existingButtons.length + 1;
              if (newCount <= allAmounts.length) {
                setDisplayedAmounts(allAmounts.slice(0, newCount));
                return;
              }
            }
            
            // If current buttons overflow, reduce count
            if (totalUsedWidth > availableWidth) {
              const reducedCount = Math.max(1, existingButtons.length - 1);
              setDisplayedAmounts(allAmounts.slice(0, reducedCount));
              return;
            }
          }
          
          // Show buttons up to the calculated limit, ensuring we don't exceed available space
          const visibleCount = Math.max(1, Math.min(safeMaxButtons, allAmounts.length));
          setDisplayedAmounts(allAmounts.slice(0, visibleCount));
        }, isSecondPass ? 50 : 100);
      });
    };

    // First pass with estimates
    calculateVisibleAmounts(false);
    
    // Second pass after buttons render to fine-tune
    const secondPassTimer = setTimeout(() => {
      calculateVisibleAmounts(true);
    }, 300);
    
    // Recalculate on window resize
    const handleResize = () => {
      calculateVisibleAmounts(false);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(secondPassTimer);
    };
  }, [isMobile, isBelow1099, allAmounts]);

  // Update displayedAmounts when custom amounts are loaded
  useEffect(() => {
    if (customAmounts && customAmounts.length > 0) {
      // On initial load, show appropriate number of amounts based on screen size
      if (isMobile) {
        setDisplayedAmounts(customAmounts.slice(0, 5));
      } else {
        // Will be recalculated by the calculateVisibleAmounts effect
        // But set initial value
        setDisplayedAmounts(customAmounts.slice(0, Math.min(6, customAmounts.length)));
      }
    }
  }, [customAmounts, isMobile]);

  // Listen for product changes and calculate product total
  useEffect(() => {
    const calculateProductTotal = () => {
      try {
        const selectedProducts = JSON.parse(sessionStorage.getItem("selectedProducts") || "[]");
        const total = selectedProducts.reduce((sum, product) => sum + (product.totalAmount || 0), 0);
        setProductTotal(total);
        const hasProductsNow = selectedProducts.length > 0;
        
        // Track if products were ever selected
        if (hasProductsNow) {
          setHadProductsBefore(true);
          setHasProducts(true);
          // When products are selected, set baseAmount to 0 only if it's still at default
          // This allows users to select a base amount even when products are selected
          if (baseAmount === initialAmount && selectedBaseAmount === initialAmount) {
            // User hasn't manually selected a different amount, so set to 0 when products are selected
            setBaseAmount(0);
          }
        } else {
          setHasProducts(false);
          // When all products are removed after being selected, keep baseAmount at 0
          // Only set to default (1000) if products were never selected (initial load)
          if (hadProductsBefore) {
            // Products were removed after being selected, keep at 0 if it was 0
            if (baseAmount === 0) {
              setBaseAmount(0);
            }
          } else {
            // Initial load with no products, use default
            if (baseAmount === 0) {
              setBaseAmount(initialAmount);
              setSelectedBaseAmount(initialAmount);
            }
          }
        }
        
        // Lock currency to INR if products are selected
        if (hasProductsNow && currency.code !== "INR") {
          setCurrency(currencyList[0]); // Set to INR
        }
      } catch (error) {
        console.error("Error calculating product total:", error);
        setProductTotal(0);
        setHasProducts(false);
        // If error and no products were ever selected, reset to default
        if (!hadProductsBefore) {
          setBaseAmount(initialAmount);
          setSelectedBaseAmount(initialAmount);
        }
      }
    };

    // Calculate on mount
    calculateProductTotal();

    // Listen for product updates
    const handleProductUpdate = () => {
      calculateProductTotal();
    };

    window.addEventListener('selectedProductsUpdated', handleProductUpdate);
    window.addEventListener('storage', handleProductUpdate);

    return () => {
      window.removeEventListener('selectedProductsUpdated', handleProductUpdate);
      window.removeEventListener('storage', handleProductUpdate);
    };
  }, [currency.code, hadProductsBefore]);

  // Validate amount - reset products if total amount is less than product total
  useEffect(() => {
    if (totalAmount < productTotal && productTotal > 0) {
      // Reset all products
      try {
        sessionStorage.setItem("selectedProducts", "[]");
        sessionStorage.setItem("selectedProductsCount", "0");
        setProductTotal(0);
        setHasProducts(false);
        // Trigger update event
        window.dispatchEvent(new CustomEvent('selectedProductsUpdated'));
      } catch (error) {
        console.error("Error resetting products:", error);
      }
    }
  }, [totalAmount, productTotal]);

  const handleCurrencyClick = (event) => {
    // For monthly donations, currency must remain INR; disable change completely
    if (isMonthlyDonation) {
      return;
    }
    // Disable currency change if products are selected
    if (hasProducts) {
      return;
    }
    if (isMobile) {
      setShowCurrencyPopup(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCurrencySelect = (cur) => {
    // For monthly donations, lock to INR and ignore selection
    if (isMonthlyDonation) {
      setAnchorEl(null);
      return;
    }
    // Don't allow currency change if products are selected
    if (hasProducts) {
      setAnchorEl(null);
      return;
    }
    setCurrency(cur);
    setAnchorEl(null);
  };

  const handleCurrencyPopupSelect = (cur) => {
    // For monthly donations, lock to INR and ignore selection
    if (isMonthlyDonation) {
      return;
    }
    // Don't allow currency change if products are selected
    if (hasProducts) {
      return;
    }
    setCurrency(cur);
  };

  const handleCloseCurrencyPopup = () => {
    setShowCurrencyPopup(false);
  };

  const handleDonate = () => {
    // Validate minimum donation amount
    const isOverseas = currency.code !== "INR";
    const validation = validateDonationAmount(totalAmount, cmsHome, isOverseas, currency.symbol);
    
    if (!validation.isValid) {
      setSnackbarMessage(validation.error);
      setSnackbarOpen(true);
      return;
    }

    // Handle monthly donations differently
    if (isMonthlyDonation) {
      if (campaignId && campaignTitle) {
        // Navigate to monthly donate page with id, title, and amount
        const params = new URLSearchParams({
          id: campaignId.toString(),
          title: campaignTitle || '',
          amount: totalAmount.toString(),
        });
        navigate(`/donate-monthly-page?${params.toString()}`);
      } else {
        navigate("/donate-monthly-page");
      }
      return;
    }

    const selected = Number(sessionStorage.getItem("selectedProductsCount") || "0");
    if (selected > 0) {
      // Store base amount in sessionStorage for product page
      sessionStorage.setItem("baseDonationAmount", String(baseAmount));
      sessionStorage.setItem("returningFromCheckout", "true");
      navigate("/donate-product-page", {
        state: {
          campaignTitle: campaignTitle || "",
          campaignId: campaignId || null,
          amount: totalAmount,
          baseAmount: baseAmount,
          currency: currency,
        },
      });
      return;
    }
    if (currency.code === "INR") {
      sessionStorage.setItem("returningFromCheckout", "true");
      navigate("/donate-once-page", {
        state: {
          campaignTitle: campaignTitle || "",
          campaignId: campaignId || null,
          amount: totalAmount,
          currency: currency,
        },
      });
    } else {
      sessionStorage.setItem("returningFromCheckout", "true");
      navigate("/overseas-donor-page", {
        state: {
          campaignTitle: campaignTitle || "",
          campaignId: campaignId || null,
          amount: totalAmount,
          currency: currency,
        },
      });
    }
  };

  const handlePaymentOptionClick = () => {
    // Validate minimum donation amount
    const isOverseas = currency.code !== "INR";
    const validation = validateDonationAmount(totalAmount, cmsHome, isOverseas, currency.symbol);
    
    if (!validation.isValid) {
      setSnackbarMessage(validation.error);
      setSnackbarOpen(true);
      return;
    }

    // Handle monthly donations differently
    if (isMonthlyDonation) {
      if (campaignId && campaignTitle) {
        // Navigate to monthly donate page with id, title, and amount
        const params = new URLSearchParams({
          id: campaignId.toString(),
          title: campaignTitle || '',
          amount: totalAmount.toString(),
        });
        navigate(`/donate-monthly-page?${params.toString()}`);
      } else {
        navigate("/donate-monthly-page");
      }
      return;
    }

    const selected = Number(sessionStorage.getItem("selectedProductsCount") || "0");
    if (selected > 0) {
      // Store base amount in sessionStorage for product page
      sessionStorage.setItem("baseDonationAmount", String(baseAmount));
      navigate("/donate-product-page", {
        state: {
          campaignTitle: campaignTitle || "",
          campaignId: campaignId || null,
          amount: totalAmount,
          baseAmount: baseAmount,
          currency: currency,
        },
      });
      return;
    }
    // Navigate to donate once page when payment option is clicked
    navigate("/donate-once-page", {
      state: {
        campaignTitle: campaignTitle || "",
        campaignId: campaignId || null,
        amount: totalAmount,
        currency: currency,
      },
    });
  };

  return (
    <>
    <Card
      sx={{
        bgcolor: "#1D1D1B",
        py: "18px",
        // py: "isBelow900 ? '10px' : '20px",
        px: isMobile ? "11px" : isTablet ? "15px" : isBelow900 ? "11px" : "20px",
        display: "flex",
        flexDirection: isMobile ? "column" : isTablet ? "column" : isBelow900 ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile 
          ? "center" 
          : isTablet 
          ? "center" 
          : isBelow1023
          ? "center"
          : isAbove1441
          ? "center"
          : "space-between",
        gap: isMobile ? "15px" : isTablet ? "15px" : isBelow1023 ? "20px" : isBelow1411 ? "5px" : "20px",
        maxWidth: "1400px",
        width: isMobile ? "100%" : isTablet ? "98%" : "98%",
        margin: "0 auto",
        borderRadius: isMobile ? "0px" : isTablet ? "16px" : "16px",
        flexWrap: isMobile ? "wrap" : isTablet ? "wrap" : "nowrap",
      }}
    >
      {/* Left Section */}
      <Box
        sx={{
          display: isBelow1023 ? "none" : "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isBelow1023 ? "15px" : isAbove1441 ? "15px" : "24px",
          maxWidth: isBelow1023 ? "100%" : "60%",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            color="#fff"
            fontWeight={700}
            sx={{
              fontSize: isBelow900 ? "14px" : isBelow1411 ? "16px" : "22px",
              minWidth: isBelow900
                ? "fit-content"
                : isBelow1411
                ? "85px"
                : "115px",
            }}
          >
            Donate Via
          </Typography>
          <Typography
            color="#fff"
             fontWeight={600}
            sx={{
              fontSize: isBelow900 ? "12px" : isBelow1411 ? "13px" : "14px",
            }}
          >
            UPI
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={isBelow900 ? "12px" : isBelow1411 ? "15px" : "35px"}
        >
          {paymentOptions.map((option, index) => (
            <Box
              key={index}
              textAlign="center"
              onClick={handlePaymentOptionClick}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <Box
                component="img"
                src={option.src}
                alt={option.name}
                sx={{
                  minWidth: 40,
                  minHeight: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <Typography
                color="#fff"
                fontSize="14px"
                fontWeight={400}
                mt={0.5}
                whiteSpace={isMobile ? "normal" : "nowrap"}
              >
                {option.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Divider */}
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          bgcolor: "#DDDDDD",
          mx: 2,
          display: isBelow1023 ? "none" : "block",
        }}
      />

      {/* Middle + Right Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "10px" : isTablet ? "8px" : isBelow900 ? "10px" : isAbove1200 ? "20px" : "10px",
          flexWrap: isMobile ? "wrap" : isTablet ? "nowrap" : "nowrap",
          justifyContent: isMobile ? "space-between" : isTablet ? "space-between" : "space-between",
          flex: 1,
          minWidth: 0, // Allow flex item to shrink below its content size
          width: isTablet ? "100%" : "auto",
        }}
      >
        {/* Middle Section - Always show, but buttons disabled when products are selected */}
        <Box
          ref={amountButtonsRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "6px" : isTablet ? "6px" : isAbove1200 ? "20px" : "10px",
            width: isMobile ? "100%" : isTablet ? "auto" : "auto",
            flexWrap: isMobile ? "nowrap" : isTablet ? "nowrap" : "nowrap",
            flex: isMobile ? "none" : isTablet ? "0 1 auto" : "1 1 0%", // Allow flex to shrink and grow
            minWidth: isTablet ? "0" : 0,
            maxWidth: "100%", // Prevent overflow
            justifyContent: isMobile ? "space-between" : isTablet ? "flex-start" : "flex-start",
            overflow: "hidden", // Prevent overflow on all screen sizes
          }}
        >
          {displayedAmounts.map((val) => (
            <Button
              key={val}
              variant="contained"
              onClick={() => {
                setBaseAmount(val);
                setSelectedBaseAmount(val);
                setHadProductsBefore(false); // Reset flag when user manually selects base amount
              }}
              sx={{
                backgroundColor: baseAmount === val ? "#F04951" : "#343432",
                color: "#fff",
                fontWeight: 700,
                borderRadius: "5px",
                textTransform: "none",
                paddingX: isMobile ? "8px" : isTablet ? "10px" : isBelow1099 ? "10px" : "20px",
                paddingY: isMobile ? "8px" : isTablet ? "10px" : isBelow1099 ? "10px" : "18px",
                fontSize: isMobile ? "11px" : isTablet ? "12px" : isBelow900 ? "12px" : "14px",
                height: isMobile ? "30px" : isTablet ? "42px" : isBelow1411 ? "48px" : "52px",
                width: isMobile ? "60px" : isTablet ? "70px" : isBelow900 ? "fit-content" : "fit-content",
                minWidth: isTablet ? "70px" : isBelow900 ? "fit-content" : "60px",
                maxWidth: isTablet ? "80px" : "none",
                flexShrink: 0, // Prevent buttons from shrinking
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: baseAmount === val ? "#F04951" : "#343432",
                },
              }}
            >
              {currency.symbol}
              {val.toLocaleString()}
            </Button>
          ))}
        </Box>

        {/* Right Section */}
        <Box
          display="flex"
          alignItems="center"
          gap={isMobile ? "10px" : isTablet ? "8px" : isBelow1411 ? "10px" : "20px"}
          width={isMobile ? "100%" : isTablet ? "auto" : "auto"}
          flexShrink={0} // Prevent shrinking on tablet
          minWidth={isTablet ? "280px" : "auto"} // Ensure minimum width on tablet
        >
          <OutlinedInput
            value={totalAmount.toLocaleString()}
            onChange={(e) => {
              // Strips commas as before, plus anything else non-numeric: a
              // pasted "5e80" used to become a real number here.
              const val = sanitizeAmountInput(e.target.value);
              const newAmount = Number(val) || 0;
              // Set base amount (total - products)
              const newBaseAmount = Math.max(0, newAmount - productTotal);
              setBaseAmount(newBaseAmount);
              // Update selected base amount if it matches a displayed amount
              if (displayedAmounts.includes(newBaseAmount)) {
                setSelectedBaseAmount(newBaseAmount);
              }
              setHadProductsBefore(false); // Reset flag when user manually enters amount
            }}
            startAdornment={
              <InputAdornment position="start">
                <Box 
                  onClick={handleCurrencyClick} 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    cursor: hasProducts ? "not-allowed" : "pointer",
                    opacity: hasProducts ? 0.6 : 1,
                    mr: 1.5,
                    gap: 0.5
                  }}
                >
                  <Typography
                    sx={{ color: "#fff", fontWeight: "700", fontSize: "18px" }}
                  >
                    {currency.symbol}
                  </Typography>
                  <Typography
                    sx={{ color: "#fff", fontWeight: 700, fontSize: "18px" }}
                  >
                    {currency.code}
                  </Typography>
                  {!hasProducts && <KeyboardArrowDownIcon sx={{ color: "#fff", fontSize: 18 }} />}
                </Box>
                <Typography
                  sx={{
                    color: "#8E8E8D",
                    ml: 1,
                    mr: 1,
                    fontWeight: "700",
                    fontSize: "18px",
                  }}
                >
                  |
                </Typography>
              </InputAdornment>
            }
            sx={{
              width: isMobile ? "100%" : isTablet ? 280 : isBelow600 ? 160 : 300, // Auto width on mobile to allow flex
              minWidth: isMobile ? "0" : isTablet ? 280 : isBelow600 ? 160 : 300, // Allow shrinking on mobile
              maxWidth: isMobile ? "calc(100% - 10px)" : "none", // Leave space for donate button on mobile
              // flex: isMobile ? "1 1 auto" : "0 0 auto", // Allow flex grow/shrink on mobile
              height: isMobile ? "45px" : isTablet ? "48px" : isBelow600 ? "48px" : "52px",
              borderRadius: "8px",
              borderColor: "#BEBFD0",
              borderStyle: "solid",
              borderWidth: "1px",
              paddingBlock: "0",
              boxSizing: "border-box", // Include padding and border in width calculation

              "& .MuiOutlinedInput-input": {
                color: "#fff",
                fontWeight: "bold",
                fontSize: isMobile ? "12px" : isTablet ? "14px" : isBelow1411 ? "14px" : "18px",
              },
              "& fieldset": {
                borderColor: "#666",
              },
            }}
          />

          {/* Desktop currency menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "10px",
                minWidth: "120px",
                "& .MuiMenuItem-root": {
                  fontSize: "16px",
                  padding: "12px 20px",
                },
              },
            }}
          >
            {currencyList.map((cur) => (
              <MenuItem 
                key={cur.code} 
                onClick={() => handleCurrencySelect(cur)}
                disabled={hasProducts && cur.code !== "INR"}
              >
                {cur.symbol} {cur.code}
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="contained"
            onClick={handleDonate}
            sx={{
              backgroundColor: "#F04951",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              paddingX: isBelow1099 ? "20px" : "20px",
              paddingY: isBelow1099 ? "10px" : "18px",
              width: isMobile ? "fit-content" : isTablet ? "100px" : isAbove1200 ? "140px" : "100px",
              minWidth: isTablet ? "100px" : "auto",
              height: isMobile ? "45px" : isTablet ? "48px" : isBelow1411 ? "48px" : "52px",
              lineHeight: "unset",
              fontSize: isMobile ? "12px" : isTablet ? "14px" : isBelow1411 ? "14px" : "18px",
              flexShrink: 0, // Prevent shrinking on tablet
              "&:hover": { backgroundColor: "#F04951" },
            }}
          >
            {isMobile ? "Donate Now" : "Donate"}
          </Button>
        </Box>
      </Box>
    </Card>
    {/* Mobile currency selector popup */}
    <CurrencySelectorPopup
      open={showCurrencyPopup}
      onClose={handleCloseCurrencyPopup}
      selectedCurrency={currency}
      onCurrencySelect={handleCurrencyPopupSelect}
    />
    {/* Snackbar for validation errors */}
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => setSnackbarOpen(false)} 
        severity="error"
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default DonationBox;
